import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router';

import { useDeferredNavigate } from '#/hooks/useDeferredNavigate';
import { useLatest } from '#/hooks/useLatest';
import { useI18n } from '#/i18n/hooks/useI18n';
import { useEmbedSearchWidget } from '#/modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';
import type { SearchRefinementType } from '#/modules/search/constants/refinements/searchRefinementTypes';
import { useSearchFiltersByName } from '#/modules/search/contexts/SearchContext';
import { useSearchAnalytics } from '#/modules/search/hooks/useSearchAnalytics';
import { searchGetRoutePathSwitch } from '#/modules/search/routing/helpers/searchGetRoutePathSwitch';
import { trackEvent } from '#/utils/analytics/track/trackEvent';

export function useSearchRefinementToggleButton(
  facetType: SearchRefinementType,
  initialToggled: boolean,
) {
  const { locale } = useI18n();
  const holderRef = useRef<HTMLDivElement>(null);
  const { embedSearchWidget } = useEmbedSearchWidget();

  const navigate = useDeferredNavigate();
  const [searchParams] = useSearchParams();
  const searchFiltersByName = useSearchFiltersByName();
  const searchParamsRef = useLatest<URLSearchParams>(searchParams);

  const { getSearchTrackingData } = useSearchAnalytics();
  const toggleButtonStatusRef = useRef(initialToggled ? 'PINNED' : 'UNPINNED');

  const toggleButton = useCallback(
    async (toggled: boolean) => {
      if (!holderRef.current) {
        throw new Error(
          `Search filter toggle button for ${facetType} does not exist.`,
        );
      }

      const { dataset } = holderRef.current;

      const filterIsPinned = dataset.facetState === 'PINNED';
      const filterIsUnpinned = dataset.facetState === 'UNPINNED';

      if (toggled && filterIsUnpinned) {
        searchParamsRef.current.append(facetType, 'true');
        dataset.facetState = 'PINNED';
      } else if (!toggled && filterIsPinned) {
        searchParamsRef.current.delete(facetType);
        dataset.facetState = 'UNPINNED';
      }

      // Toggling a filter should always reset results to the first page
      if (searchParamsRef.current.has('page')) {
        searchParamsRef.current.delete('page');
      }

      trackEvent('Changed Search Filter', {
        filter_name: facetType,
        selected_value: toggled,
        ...getSearchTrackingData(),
      });

      const newRoute = searchGetRoutePathSwitch({
        locale,
        q: searchParamsRef.current.get('q') ?? '',
        filtersByName: {
          ...searchFiltersByName,
          [facetType]: toggled || undefined,
        },
        embedSearchWidgetId: embedSearchWidget?.id ?? null,
      });

      await navigate(newRoute, {
        replace: true,
      });
    },
    [
      locale,
      facetType,
      getSearchTrackingData,
      navigate,
      searchFiltersByName,
      searchParamsRef,
      embedSearchWidget?.id,
    ],
  );

  return {
    holderRef,
    toggleButton,
    toggleButtonStatus: toggleButtonStatusRef.current,
  };
}
